import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DeliveryDriver from '../DeliveryDriver';
import DeliveryListHeader from './DeliveryListHeader';
import DeliveryItem from './DeliveryItem';
import NextDeliveryFooter from './NextDeliveryFooter';
import PackageScannerModal from './PackageScannerModal';
import BulkReceiveModal from './BulkReceiveModal';
import PackageHistoryModal from './PackageHistoryModal';
import { useDriverOnWayNotification } from './useDriverOnWayNotification';

function DeliveryList({ setActiveComponent, userId }) {
  const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [driver, setDriver] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeStarted, setRouteStarted] = useState(false);
  
  // Modal states
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryDelivery, setSelectedHistoryDelivery] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchDeliveryPoints();
      fetchDriverData();
    }
  }, [userId]);

  const fetchDriverData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/location/driver`, {
        params: { driver_id: userId },
      });
      setDriver(response.data);
    } catch (error) {
      console.error('Error fetching driver:', error);
    }
  };

  const fetchDeliveryPoints = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/delivery_points/driver/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDeliveries(response.data);
      
      // Check if route has been started
      const hasStartedRoute = response.data.some(
        d => !d.is_submitted && d.status && d.status.toLowerCase() !== 'pending'
      );
      setRouteStarted(hasStartedRoute);
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    }
  }, [userId]);

  // Use custom hook for driver on way notifications
  useDriverOnWayNotification(deliveries, setDeliveries);

  const handleDeliveriesRoutes = async () => {
    if (loadingRoutes || !driver) return;

    const pendingDeliveries = deliveries.filter(
      d => d.status === 'pending' && !d.is_submitted
    );
    
    if (pendingDeliveries.length === 0) {
      alert('No hay entregas pendientes para procesar.');
      return;
    }

    const sortedDeliveries = pendingDeliveries.sort((a, b) => a.sequence - b.sequence);

    const startPoint = {
      lat: driver.latitude,
      lng: driver.longitude,
    };

    const endPoint = {
      lat: sortedDeliveries[sortedDeliveries.length - 1].latitude,
      lng: sortedDeliveries[sortedDeliveries.length - 1].longitude,
    };

    const stops = sortedDeliveries.slice(0, -1).map(delivery => ({
      lat: delivery.latitude,
      lng: delivery.longitude,
    }));

    const deliveryIds = sortedDeliveries.map(delivery => delivery.id);

    setLoadingRoutes(true);

    try {
      await createGoogleRoute(startPoint, endPoint, stops, deliveryIds);
      await fetchDeliveryPoints();
      setRouteStarted(true);
      alert('¡Empezaste tu ruta, suerte!');
    } catch (error) {
      console.error('Error updating arrival times:', error);
      alert('Algo falló, por favor intenta de nuevo.');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const createGoogleRoute = async (startPoint, endPoint, waypoints, deliveryIds) => {
    const token = localStorage.getItem('token');
    const formattedWaypoints = waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|');

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/create_route_deliveries`,
      {
        startPoint: `${startPoint.lat},${startPoint.lng}`,
        endPoint: `${endPoint.lat},${endPoint.lng}`,
        waypoints: formattedWaypoints,
        deliveryIds: deliveryIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.data.message !== 'Arrival times updated successfully') {
      throw new Error('Failed to create route with Google Maps API');
    }

    return response.data;
  };

  /**
   * Handle package scanned - refresh delivery list
   */
  const handlePackageScanned = (data) => {
    // Refresh delivery list to update status icons
    fetchDeliveryPoints();
  };

  /**
   * Handle bulk receive - refresh delivery list
   */
  const handleBulkReceived = (data) => {
    // Refresh delivery list to update status icons
    fetchDeliveryPoints();
  };

  /**
   * Show package history modal
   */
  const handleShowHistory = (delivery) => {
    setSelectedHistoryDelivery(delivery);
    setShowHistoryModal(true);
  };

  // Separate deliveries into active and completed
  const activeDeliveries = deliveries
    .filter(d => !d.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  const completedDeliveries = deliveries
    .filter(d => d.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  // Find next delivery
  const nextDelivery = activeDeliveries[0];

  // Check if can start route
  const nonSubmittedDeliveries = deliveries.filter(d => !d.is_submitted);
  const allPending = nonSubmittedDeliveries.every(d => d.status === 'pending');
  const canStartRoute = allPending && nonSubmittedDeliveries.length > 0 && !routeStarted;

  const handleBackFromDelivery = () => {
    setSelectedDeliveryPoint(null);
    fetchDeliveryPoints();
  };

  if (selectedDeliveryPoint) {
    return (
      <DeliveryDriver
        delivery={selectedDeliveryPoint}
        onBack={handleBackFromDelivery}
        setActiveComponent={setActiveComponent}
        driverId={userId}
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <DeliveryListHeader
        setActiveComponent={setActiveComponent}
        onStartRoute={handleDeliveriesRoutes}
        canStartRoute={canStartRoute}
        loadingRoutes={loadingRoutes}
        routeStarted={routeStarted}
        onOpenScanner={() => setShowScannerModal(true)}
        onOpenBulkReceive={() => setShowBulkReceiveModal(true)}
      />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-24">
        <h1 className="text-3xl font-montserrat font-bold text-brand-primary px-5 py-4">
          Entregas
        </h1>

        {/* Active Deliveries */}
        {activeDeliveries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Activas
            </h2>
            <div className="bg-white">
              {activeDeliveries.map((delivery, index) => (
                <DeliveryItem
                  key={delivery.id}
                  delivery={delivery}
                  onClick={() => setSelectedDeliveryPoint(delivery)}
                  isLast={index === activeDeliveries.length - 1}
                  onShowHistory={handleShowHistory}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Deliveries */}
        {completedDeliveries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Completadas
            </h2>
            <div className="bg-white opacity-60">
              {completedDeliveries.map((delivery, index) => (
                <DeliveryItem
                  key={delivery.id}
                  delivery={delivery}
                  onClick={() => {}}
                  isLast={index === completedDeliveries.length - 1}
                  disabled={true}
                  onShowHistory={handleShowHistory}
                />
              ))}
            </div>
          </div>
        )}

        {deliveries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay entregas asignadas
          </div>
        )}
      </div>

      {/* Fixed Footer - Next Delivery Quick Access */}
      {nextDelivery && (
        <NextDeliveryFooter
          delivery={nextDelivery}
          onClick={() => setSelectedDeliveryPoint(nextDelivery)}
        />
      )}

      {/* Modals */}
      <PackageScannerModal
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        driverId={userId}
        onPackageScanned={handlePackageScanned}
      />

      <BulkReceiveModal
        isOpen={showBulkReceiveModal}
        onClose={() => setShowBulkReceiveModal(false)}
        driverId={userId}
        deliveries={deliveries}
        onBulkReceived={handleBulkReceived}
      />

      <PackageHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        deliveryPointId={selectedHistoryDelivery?.id}
        trackingCode={selectedHistoryDelivery?.tracking_code}
      />
    </div>
  );
}

export default DeliveryList;
